import type { Icon } from '@phosphor-icons/react'
import {
  Sun,
  CloudSun,
  Cloud,
  Sparkle,
  Moon,
  Leaf,
  Heartbeat,
  Warning,
  FirstAid,
  Drop,
  Flask,
  Scissors,
  PottedPlant,
  SprayBottle,
  ArrowsClockwise,
  Broom,
  Bug,
  Wrench,
  Plant,
  Gradient,
  TestTube,
  GearSix,
  Package,
  CloudRain,
  CloudSnow,
  CloudFog,
  Lightning,
  Snowflake,
  Flower,
  House,
  Camera,
  Asterisk,
} from '@phosphor-icons/react'

// --- Types ---

export interface IconEntry {
  icon: Icon
  color: string
  count?: number
}

// --- Helper component ---

export function EnumIcon({
  entry,
  size = 16,
  className,
}: {
  entry: IconEntry | undefined
  size?: number
  className?: string
}) {
  if (!entry) return null
  const Ic = entry.icon
  const weight = size < 20 ? 'bold' : 'regular'
  const cls = className || entry.color
  const count = entry.count || 1
  if (count === 1) return <Ic weight={weight} size={size} className={cls} />
  return (
    <span className="inline-flex items-center" style={{ gap: `${Math.max(1, size * -0.15)}px` }}>
      {Array.from({ length: count }, (_, i) => (
        <Ic key={i} weight={weight} size={size} className={cls} />
      ))}
    </span>
  )
}

// --- Enum mappings ---

export const SUNLIGHT_ICONS: Record<string, IconEntry> = {
  full_sun: { icon: Sun, color: 'text-danger' },
  indirect: { icon: Sparkle, color: 'text-ochre' },
  partial_shade: { icon: Cloud, color: 'text-water' },
  shade: { icon: Moon, color: 'text-ink' },
}

export const DIFFICULTY_ICONS: Record<string, IconEntry> = {
  easy: { icon: Leaf, color: 'text-botanical', count: 1 },
  medium_difficulty: { icon: Leaf, color: 'text-ochre', count: 2 },
  hard: { icon: Leaf, color: 'text-danger', count: 3 },
}

export const STATUS_ICONS: Record<string, IconEntry> = {
  healthy: { icon: Heartbeat, color: 'text-botanical' },
  needs_attention: { icon: Warning, color: 'text-ochre' },
  sick: { icon: FirstAid, color: 'text-danger' },
  dormant: { icon: Moon, color: 'text-frame' },
}

export const CARE_ACTION_ICONS: Record<string, IconEntry> = {
  watering: { icon: Drop, color: 'text-water' },
  fertilizing: { icon: Flask, color: 'text-ochre' },
  pruning: { icon: Scissors, color: 'text-botanical' },
  repotting: { icon: PottedPlant, color: 'text-frame' },
  misting: { icon: SprayBottle, color: 'text-botanical' },
  rotating: { icon: ArrowsClockwise, color: 'text-ink-light' },
  cleaning: { icon: Broom, color: 'text-ink-light' },
  pest_check: { icon: Bug, color: 'text-danger' },
  custom: { icon: Wrench, color: 'text-frame' },
}

export const LIGHT_LEVEL_ICONS: Record<string, IconEntry> = {
  bright_direct: { icon: Sun, color: 'text-danger' },
  bright_indirect: { icon: Sparkle, color: 'text-ochre' },
  medium: { icon: Cloud, color: 'text-water' },
  low: { icon: Moon, color: 'text-ink' },
}

export const HUMIDITY_LEVEL_ICONS: Record<string, IconEntry> = {
  high: { icon: Drop, color: 'text-water', count: 3 },
  medium_humidity: { icon: Drop, color: 'text-water', count: 2 },
  medium: { icon: Drop, color: 'text-water', count: 2 },
  low_humidity: { icon: Drop, color: 'text-water', count: 1 },
  low: { icon: Drop, color: 'text-water', count: 1 },
}

export const SUPPLY_CATEGORY_ICONS: Record<string, IconEntry> = {
  substrate: { icon: Gradient, color: 'text-frame' },
  fertilizer: { icon: TestTube, color: 'text-botanical' },
  amendment: { icon: GearSix, color: 'text-terracotta' },
  tool: { icon: Wrench, color: 'text-concrete' },
  other: { icon: Package, color: 'text-ochre' },
}

export const SEVERITY_ICONS: Record<string, IconEntry> = {
  info: { icon: Warning, color: 'text-botanical', count: 1 },
  mild: { icon: Warning, color: 'text-ochre', count: 1 },
  moderate: { icon: Warning, color: 'text-ochre', count: 2 },
  severe: { icon: Warning, color: 'text-danger', count: 3 },
}

// --- Placeholder icons ---

export const PLACEHOLDER_ICONS = {
  plant: { icon: Flower, color: 'text-frame/40' },
  pot: { icon: PottedPlant, color: 'text-frame/40' },
  location: { icon: House, color: 'text-frame/40' },
  species: { icon: Leaf, color: 'text-frame/40' },
  supply: { icon: Package, color: 'text-frame/40' },
  camera: { icon: Camera, color: 'text-frame' },
}

// --- Decorative ---

export { Asterisk as SeparatorIcon } from '@phosphor-icons/react'

// --- Weather ---

const WMO_WEATHER: Record<number, { day: Icon; night: Icon; color: string }> = {
  0: { day: Sun, night: Moon, color: 'text-ochre' },
  1: { day: CloudSun, night: Moon, color: 'text-ochre' },
  2: { day: CloudSun, night: Cloud, color: 'text-frame' },
  3: { day: Cloud, night: Cloud, color: 'text-frame' },
  45: { day: CloudFog, night: CloudFog, color: 'text-ink-light' },
  48: { day: CloudFog, night: CloudFog, color: 'text-ink-light' },
  51: { day: CloudRain, night: CloudRain, color: 'text-water' },
  53: { day: CloudRain, night: CloudRain, color: 'text-water' },
  55: { day: CloudRain, night: CloudRain, color: 'text-water' },
  61: { day: CloudRain, night: CloudRain, color: 'text-water' },
  63: { day: CloudRain, night: CloudRain, color: 'text-water' },
  65: { day: CloudRain, night: CloudRain, color: 'text-water' },
  66: { day: CloudSnow, night: CloudSnow, color: 'text-danger' },
  67: { day: CloudSnow, night: CloudSnow, color: 'text-danger' },
  71: { day: Snowflake, night: Snowflake, color: 'text-frame' },
  73: { day: CloudSnow, night: CloudSnow, color: 'text-frame' },
  75: { day: CloudSnow, night: CloudSnow, color: 'text-frame' },
  77: { day: Snowflake, night: Snowflake, color: 'text-frame' },
  80: { day: CloudRain, night: CloudRain, color: 'text-water' },
  81: { day: CloudRain, night: CloudRain, color: 'text-water' },
  82: { day: CloudRain, night: CloudRain, color: 'text-water' },
  85: { day: CloudSnow, night: CloudSnow, color: 'text-frame' },
  86: { day: CloudSnow, night: CloudSnow, color: 'text-frame' },
  95: { day: Lightning, night: Lightning, color: 'text-danger' },
  96: { day: Lightning, night: Lightning, color: 'text-danger' },
  99: { day: Lightning, night: Lightning, color: 'text-danger' },
}

export function WeatherIcon({
  code,
  isDay = true,
  size = 24,
  className,
}: {
  code: number
  isDay?: boolean
  size?: number
  className?: string
}) {
  const entry = WMO_WEATHER[code] || WMO_WEATHER[0]
  const Ic = isDay ? entry.day : entry.night
  return <Ic weight={size < 20 ? 'bold' : 'regular'} size={size} className={className || entry.color} />
}
